import { yupResolver } from '~/helpers/yupResolver';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from 'urql';
import * as Yup from 'yup';
import { BrandSquare } from '~/brand/BrandContext';
import { Input } from '~/components/form/Input';
import { Label } from '~/components/ui/Label';
import { graphql } from '~/gql';
import {
  getValidatedRedirectFromStorage,
  validateRedirect,
} from '~/helpers/redirect';
import { LogoSection } from '../LogoSection';
import { LoginUsingPassword } from './LoginUsingPassword';
import { VerifyForm } from './VerifyForm';
import {
  displayEmail,
  isValidLoginIdentifier,
  normalizeLoginIdentifier,
} from './loginIdentifier';

type FormValues = {
  identifier: string;
  method?: 'email' | 'sms';
};

const OtpGenerate_Mutation = graphql(`
  mutation OtpGenerate($input: OtpGenerateInput!) {
    otpGenerate(input: $input) {
      success
      id
    }
  }
`);

const validationSchema = Yup.object().shape({
  // TODO figure out how to validate email or mobile number
  identifier: Yup.mixed()
    .required('Required')
    .test(
      'is-valid',
      'Enter a valid email or mobile number',
      isValidLoginIdentifier
    )
    .transform((value) => normalizeLoginIdentifier(value)),
});

export const LoginForm = () => {
  const { t } = useTranslation('auth');
  const [result, otpGenerate] = useMutation(OtpGenerate_Mutation);
  const [searchParams] = useSearchParams();
  const [loginWithPassword, setLoginWithPassword] = useState(
    searchParams.get('pw') === '1' ? true : false
  );
  const [verify, setVerify] = useState<string | null>(null);

  const jobId = searchParams.get('j');
  const email = searchParams.get('e');
  const phone = searchParams.get('p');
  const token = searchParams.get('t');
  const redirectParam = searchParams.get('redirect');

  // Validate and sanitize the redirect parameter to prevent open-redirect vulnerabilities
  // Returns null if validation fails (validateRedirect returns empty string fallback)
  const sanitizedRedirect = redirectParam
    ? validateRedirect(redirectParam, '') || null
    : null;

  // Store sanitized redirect param in sessionStorage for use after login (if provided)
  // This allows the redirect to persist across the login flow
  // Using useEffect to avoid side effects during render
  useEffect(() => {
    if (sanitizedRedirect) {
      try {
        window.sessionStorage.setItem(
          'redirectTo',
          JSON.stringify({
            pathname: sanitizedRedirect,
            t: new Date().getTime(),
          })
        );
      } catch {
        // Error handling: sessionStorage may not be available
      }
    }
  }, [sanitizedRedirect]);

  const {
    register,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<FormValues>({
    defaultValues: token
      ? {
          identifier: token,
          method: 'email',
        }
      : {
          identifier: '',
        },
    resolver: yupResolver(validationSchema),
  });

  const identifierValue = watch('identifier');

  const submit = async (values: FormValues) => {
    const { data } = await otpGenerate({
      input: values,
    });
    if (data?.otpGenerate.success && data.otpGenerate.id) {
      setVerify(data.otpGenerate.id);
    }
  };

  // const isMobile = value === '' || /^(\+?\d[\d\s]*)+$/.test(value);
  // console.log('isMobile', isMobile);

  if (loginWithPassword) {
    // Extract email from identifier if it contains '@', otherwise pass undefined
    const emailToPass =
      identifierValue && identifierValue.includes('@')
        ? identifierValue
        : undefined;
    return (
      <LoginUsingPassword
        back={() => setLoginWithPassword(false)}
        email={emailToPass}
      />
    );
  }

  if (verify) {
    // Priority: sanitized redirect param > job id > sessionStorage fallback > null
    const verifyRedirectTo =
      sanitizedRedirect ??
      (jobId ? `/jobs/${jobId}` : getValidatedRedirectFromStorage() ?? null);
    return (
      <VerifyForm
        id={verify}
        redirectTo={verifyRedirectTo}
        onBack={setVerify}
      />
    );
  }

  const input = token ? (
    <>
      <Label as='div'>Send code via</Label>
      <label className='mb-3 ml-2 mt-1 flex gap-2'>
        <input type='radio' value='email' {...register('method')} />
        Email to {email ? displayEmail(email) : 'hidden'}
      </label>
      {phone && (
        <label className='mb-3 ml-2 mt-1 flex gap-2'>
          <input type='radio' value='sms' {...register('method')} />
          SMS to number ending in {phone}
        </label>
      )}
    </>
  ) : (
    <Input
      {...register('identifier')}
      label={t('emailOrMobileNumber')}
      type='text'
      error={errors?.identifier?.message}
    />
  );

  return (
    <div className='flex h-screen w-screen items-center justify-center'>
      <div className='w-full lg:w-1/2'>
        <form
          onSubmit={handleSubmit(submit)}
          className='mx-auto flex flex-1 flex-col justify-center rounded-lg bg-white px-10 py-16 md:w-4/5 md:border md:border-gray88 lg:mx-auto lg:w-[30rem] lg:flex-initial'
        >
          <div className='mb-12 flex flex-col items-center justify-center'>
            <BrandSquare className='mx-auto h-20' />
            <h1 className='mt-5 text-center text-2xl font-medium'>
              {t('signInText')}
            </h1>
          </div>

          {input}

          <button
            className='w-full rounded-lg bg-brand p-3 font-semibold uppercase text-white transition-all duration-300 hover:bg-slate-dark  disabled:opacity-50'
            disabled={result.fetching}
          >
            {t('continue')}
          </button>

          <div className='m-4 inline-flex h-6 items-center justify-center gap-2.5'>
            <div className='h-px w-full bg-black'></div>
            <div className="text-center font-['Figtree'] text-base font-medium leading-snug">
              OR
            </div>
            <div className='h-px w-full bg-black'></div>
          </div>

          <button
            type='button'
            onClick={() => setLoginWithPassword(true)}
            className='mb-4 w-full rounded-lg border border-brand bg-white p-3 font-semibold uppercase text-brand'
          >
            log in with password
          </button>
          <div className='mt-4 flex flex-col items-center gap-2 text-center'>
            {' '}
            <div className=''>
              <Link className='font-normal text-brand underline' to='/signup'>
                {t('signUp')}
              </Link>
            </div>
          </div>
        </form>{' '}
      </div>

      <LogoSection />
    </div>
  );
};
